import { Badge, Heading, HStack, VStack } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { LinkBox, LinkOverlay } from "@chakra-ui/react/link"
import { Text } from "@chakra-ui/react/text"

import type { HomeWorkItem } from "@/types/getHome"

import Img from "@/components/Img"
import MarkdownParser from "@/components/MarkdownParser"
import NextLink from "@/components/RouteTransitionLink"

import WorkThumbnailFallback from "./WorkThumbnailFallback"

interface FeaturedWorkCardProps extends React.ComponentPropsWithoutRef<
  typeof LinkBox
> {
  index: number
  item: HomeWorkItem
  showTags?: boolean
  /**
   * Hint to the image loader. Defaults to true (matches previous home page
   * behaviour). Set false in lists where the card is unlikely to be in the
   * initial viewport (e.g. paginated work index).
   */
  priority?: boolean
  imageProps?: React.ComponentPropsWithoutRef<typeof Box>
}

const WorkCardCategoryBadges = ({ item }: { item: HomeWorkItem }) => (
  <HStack flexWrap="wrap" gap="0.5rem">
    <Text color={"fg.muted"} fontFamily={"mono"} fontSize={"xs"}>
      {item.eyebrow} /{" "}
      {item.dateLabel && (
        <Text as="span" color={"fg.muted"} fontSize={"xs"}>
          {item.dateLabel}
        </Text>
      )}
    </Text>
  </HStack>
)

const WorkCardMedia = ({
  index,
  item,
  priority,
  ...imageProps
}: {
  index: number
  item: HomeWorkItem
  priority: boolean
  imageProps?: React.ComponentPropsWithoutRef<typeof Box>
}) => (
  <Box
    bg="bg.muted"
    className="work-card-media"
    css={{
      "& img": {
        transition: "filter 300ms ease, transform 400ms ease",
      },
    }}
    flexShrink="0"
    overflow="hidden"
    position="relative"
    rounded="0"
    {...imageProps}
  >
    {item.image ? (
      <Img
        alt={
          (item.title && `Thumbnail image for ${item.title}`) ||
          "Work thumbnail"
        }
        breakpointWidths={{
          base: "310",
          sm: "340",
          md: "360",
          lg: "400",
          xl: "460",
        }}
        fetchPriority={priority ? "high" : "auto"}
        height="100%"
        noContainer
        priority={priority}
        src={item.image}
        transform={{ format: "auto", quality: 100 }}
        width="100%"
      />
    ) : (
      <WorkThumbnailFallback index={index} />
    )}
  </Box>
)

const WorkSkillBadges = ({ skills }: { skills?: HomeWorkItem["skills"] }) => {
  const MAX_VISIBLE_SKILLS = 5
  const visibleSkills = skills?.slice(0, MAX_VISIBLE_SKILLS)
  const remainingSkillCount =
    skills && skills.length > MAX_VISIBLE_SKILLS
      ? skills.length - MAX_VISIBLE_SKILLS
      : 0

  if (!visibleSkills || visibleSkills.length === 0) {
    return null
  }

  return (
    <HStack
      as="ul"
      flexWrap="wrap"
      gap="0.375rem"
      listStyleType="none"
      m="0"
      p="0"
    >
      {visibleSkills.map((skill) => (
        <Badge
          aria-label={skill.name}
          as="li"
          colorPalette="gray"
          key={skill.id || skill.slug || skill.name}
          rounded="full"
          size="sm"
          variant="subtle"
          w="fit-content"
        >
          {skill.name}
        </Badge>
      ))}
      {remainingSkillCount > 0 && (
        <Badge
          aria-label={`${remainingSkillCount} more skills`}
          as="li"
          colorPalette="gray"
          rounded={"full"}
          size="sm"
          variant="surface"
          w="fit-content"
        >
          +{remainingSkillCount}
        </Badge>
      )}
    </HStack>
  )
}

const FeaturedWorkCard = ({
  imageProps,
  index,
  item,
  priority = true,
  showTags = false,
  ...rest
}: FeaturedWorkCardProps) => {
  const headline = item.headline?.trim()

  return (
    <LinkBox
      _first={{
        borderTop: "1px solid {colors.border}",
      }}
      _focusWithin={{
        outline: "2px solid {colors.primary.500}",
        outlineOffset: "-1px",
        rounded: 0,
      }}
      _hover={{
        base: { bgColor: "bg.emphasized" },
        _dark: { bgColor: "bg.muted" },
        "& .article-title": {
          backgroundSize: "100% 1px",
        },
      }}
      as="article"
      borderBottom="1px solid {colors.border}"
      className="group"
      p={{ base: 1, md: 2, lg: 4 }}
      position="relative"
      rounded="4xl"
      transitionDuration="slow"
      transitionProperty="background"
      transitionTimingFunction="ease-in-out"
      {...rest}
    >
      <Flex
        alignItems="stretch"
        flexDirection={{ base: "column", lg: "row" }}
        flexWrap="wrap"
        gap={{ base: "1.25rem", sm: "1.75rem" }}
      >
        <VStack
          alignItems="flex-start"
          flex="1"
          gap={{ base: "3", md: "3.5" }}
          minW="0"
          order={{ base: 2, sm: 2, lg: 1 }}
          pl={{ base: 0, md: 1, lg: 3 }}
          px={{ base: 3, md: 1, lg: 3 }}
          w={{ base: "full", sm: "auto" }}
        >
          <HStack flexWrap="wrap" gap="0" mt="2">
            <WorkCardCategoryBadges item={item} />
          </HStack>
          <LinkOverlay
            alignItems={"center"}
            asChild
            display="inline-flex"
            overflow="hidden"
            w={"full"}
            zIndex={1}
          >
            <NextLink
              href={item.href}
              title={item.title}
              transitionTypes={["nav-forward"]}
            >
              <Heading
                alignItems="center"
                as="h3"
                backgroundImage="linear-gradient(currentColor, currentColor)"
                backgroundPosition="0 100%"
                backgroundRepeat="no-repeat"
                backgroundSize={"0% 1px"}
                className="article-title"
                color="fg"
                display="inline-flex"
                fontWeight={{ base: "500", sm: "600" }}
                lineClamp="2"
                lineHeight="1.3"
                size={"2xl"}
                transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
                w={"auto"}
              >
                {item.title}
              </Heading>
            </NextLink>
          </LinkOverlay>

          <VStack alignItems="flex-start" flexGrow={1} gap="6">
            {headline && (
              <Box display="box" lineClamp="4" overflow="hidden" w="full">
                <MarkdownParser
                  color="fg.muted"
                  fontSize="lg"
                  headingProps={{
                    as: "p",
                    color: "fg.muted",
                    fontFamily: "body",
                    fontSize: "lg",
                    fontWeight: "normal",
                    lineClamp: "2",
                    lineHeight: "1.6",
                    marginBottom: 0,
                    overflow: "hidden",
                    size: "md",
                  }}
                  marginBottom={0}
                  markdown={headline}
                  pProps={{
                    color: "fg.muted",
                    display: "box",
                    fontSize: { base: "sm", md: "md" },
                    lineClamp: "2",
                    lineHeight: "1.6",
                    marginBottom: 0,
                    overflow: "hidden",
                  }}
                  strongProps={{
                    color: "fg.muted",
                    fontSize: "inherit",
                  }}
                />
              </Box>
            )}

            {item.evidenceMetric && (
              <HStack alignItems="center" gap="0">
                <Text
                  bgColor={{ base: "green.50", _dark: "green.950" }}
                  border={{
                    base: "1px solid {colors.green.100}",
                    _dark: "1px solid {colors.green.800}",
                  }}
                  color={{ base: "green.800", _dark: "green.200" }}
                  fontSize="sm"
                  lineHeight="1.45"
                  mb={{ base: 3, lg: 0 }}
                  px="4"
                  py={1.5}
                  rounded={"4xl"}
                >
                  {item.evidenceMetric}
                </Text>
              </HStack>
            )}
          </VStack>
          {showTags && item.skills && item.skills.length > 0 && (
            <VStack mb="2">
              <WorkSkillBadges skills={item.skills} />
            </VStack>
          )}
        </VStack>

        <Box
          className="article-img"
          css={{
            ".group:hover & .work-card-arrow": {
              backgroundColor: "var(--gil-colors-primary-500)",
              color: "white",
              transform: "translateX(0.25rem)",
            },
            ".group:hover & .work-card-media img": {
              filter: "brightness(0.75)",
              transform: "scale(1.02)",
            },
          }}
          order={{ base: 1 }}
          pb={{ base: 0, md: 1, lg: 0 }}
          pt={{ base: 1, md: 0, lg: 0 }}
          px={{ base: 1, md: 0, lg: 0 }}
          w={{ base: "full", lg: "auto" }}
        >
          <WorkCardMedia
            index={index}
            item={item}
            priority={priority}
            {...imageProps}
          />
        </Box>
      </Flex>
    </LinkBox>
  )
}

export default FeaturedWorkCard
